// Constants
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
import { createIdentitySet } from "../../../utilities/identity";
import { BucketExtensionKeys } from "../../../service/planner/extensions/IBucketExtensionsResource";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
/**
 * The bucket entity implementation.
 */ export class Bucket extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, _source) {
        return {
            displayName: changed.displayName,
            color: changed.bucketColor !== undefined ? changed.bucketColor != null ? changed.bucketColor.replace("#", "") : null : undefined,
            extensions: changed.extensions && this.serializeExtensionsDiff(changed.extensions)
        };
    }
    getPropertyMappingOverride() {
        return {
            bucketColor: "color"
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            bucketColor: (value)=>value ? `#${value}` : null,
            creationInfo: (value)=>({
                    ...value,
                    createdDateTime: Temporal.Instant.from(value.createdDateTime)
                }),
            extensions: (value)=>Bucket.deserializeExtensions(value),
            lastModifiedDateTime: (value)=>Temporal.Instant.from(value)
        });
    }
    static fromResource(resource) {
        return new Bucket({
            id: resource.id,
            version: resource["@etag"],
            planId: resource.planId,
            displayName: resource.displayName,
            creationInfo: {
                ...resource.creationInfo,
                createdBy: createIdentitySet(resource.creationInfo.createdBy),
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime)
            },
            bucketColor: resource.color ? `#${resource.color}` : null,
            extensions: this.deserializeExtensions(resource.extensions),
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime)
        });
    }
    /**
     * Deserializes the extensions of the bucket.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        const output = {
            orderHint: DefaultOrderHint
        };
        // First parse the legacy data if it exists
        const serializedLegacyData = extensions[BucketExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                output.orderHint = legacyExtensionData.orderHint;
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=legacyData]`);
            }
        }
        return output;
    }
    getClone() {
        return new Bucket({
            id: this.id,
            version: this.version,
            planId: this.planId,
            displayName: this.displayName,
            creationInfo: {
                ...cloneDeep(this.creationInfo),
                createdDateTime: Temporal.Instant.from(this.creationInfo.createdDateTime)
            },
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            bucketColor: this.bucketColor,
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forCreate() {
        return {
            planId: this.planId,
            displayName: this.displayName,
            ...this.bucketColor != null && {
                color: this.bucketColor.replace("#", "")
            },
            extensions: this.serializeExtensions()
        };
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            planId: this.planId,
            displayName: this.displayName,
            color: this.bucketColor != null ? this.bucketColor.replace("#", "") : null,
            creationInfo: {
                ...this.creationInfo,
                createdDateTime: this.creationInfo.createdDateTime.toString()
            },
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            extensions: this.serializeExtensions()
        };
    }
    getOrderHint() {
        return this.extensions.orderHint;
    }
    setOrderHint(value) {
        const updatedExtensions = {
            ...cloneDeep(this.extensions),
            orderHint: value
        };
        const clone = this.getClone();
        clone.extensions = updatedExtensions;
        return clone;
    }
    /**
     * Sets the bucket color and returns a new bucket instance.
     * @param color The hex background color string (e.g. "#d8cce7"), or `null` to clear the color
     * @returns A new IBucket instance with the updated color
     */ setBucketColor(color) {
        return this.setProperty("bucketColor", color);
    }
    /**
     * Serializes only the changed extension fields for use in PATCH requests.
     * Uses the diff output directly to avoid redundant comparisons.
     * @param diff The diff containing only the changed extension properties
     */ serializeExtensionsDiff(diff) {
        const output = {};
        // OrderHint: only include if it changed.
        if (diff.orderHint !== undefined) {
            const legacyData = {
                orderHint: this.extensions.orderHint
            };
            output[BucketExtensionKeys.legacyData] = JSON.stringify(legacyData);
        }
        return output;
    }
    /**
     * Serializes the extensions of the bucket.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        // Always serialize to both Legacy and Evo formats to support round-trip deserialization across v3 and v4 apis
        const legacyData = {
            orderHint: this.extensions.orderHint
        };
        output[BucketExtensionKeys.legacyData] = JSON.stringify(legacyData);
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.planId = propertyBag.planId;
        this.displayName = propertyBag.displayName;
        this.creationInfo = propertyBag.creationInfo ?? {
            createdDateTime: Temporal.Now.instant(),
            createdBy: propertyBag.lastModifiedBy,
            externalId: null,
            externalVersion: null,
            scenarioId: null,
            scope: null,
            externalUrl: null
        };
        this.lastModifiedBy = propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ?? Temporal.Now.instant();
        this.bucketColor = propertyBag.bucketColor ?? null;
        this.extensions = propertyBag.extensions ?? {
            orderHint: DefaultOrderHint
        };
    }
}
/** Matches BucketLogic.cs : NameSizeLimit */ Bucket.TitleMaxLength = 256;
